<template>
    <AmModal
        :open="open"
        size="large"
        eyebrow="ТАРИФЫ"
        accent-eyebrow
        title="Выберите подходящий план"
        subtitle="Расширьте лимиты запросов и подключите дополнительные источники. Можно отменить в любой момент."
        @close="$emit('close')"
    >
            <div v-if="loading" class="state-block">Загружаем актуальные тарифы…</div>
            <div v-else-if="error" class="state-block state-block--error">
                Не удалось загрузить тарифы. Попробуйте обновить страницу.
            </div>
            <div v-else-if="visiblePlans.length === 0" class="state-block">
                Сейчас тарифы недоступны — обратитесь к менеджеру.
            </div>

            <div v-else class="plans">
                <article
                    v-for="planItem in visiblePlans"
                    :key="planItem.code"
                    class="plan-card"
                    :class="{
                        'plan-card--current': planItem.code === currentPlan,
                        'plan-card--accent': planItem.code === accentCode,
                        'plan-card--coming-soon': planItem.is_coming_soon,
                    }"
                >
                    <div v-if="planItem.is_coming_soon" class="plan-tag plan-tag--soon">Скоро</div>
                    <div v-else-if="planItem.code === accentCode" class="plan-tag">Популярный</div>

                    <div class="plan-name">{{ planItem.name }}</div>
                    <div class="plan-price">
                        <span class="price-value">{{ formatPrice(planItem) }}</span>
                        <span v-if="formatPriceSuffix(planItem)" class="price-suffix">
                            {{ formatPriceSuffix(planItem) }}
                        </span>
                    </div>
                    <div class="plan-note">{{ formatPlanNote(planItem) }}</div>

                    <ul class="plan-features">
                        <li
                            v-for="(feature, idx) in buildFeatures(planItem)"
                            :key="idx"
                            class="plan-feature"
                            :class="{ 'plan-feature--off': feature.off }"
                        >
                            <span class="feature-mark" aria-hidden="true">{{ feature.off ? '—' : '✓' }}</span>
                            <span>{{ feature.text }}</span>
                        </li>
                    </ul>

                    <button
                        v-if="planItem.code === currentPlan"
                        type="button"
                        class="plan-cta plan-cta--current"
                        disabled
                    >
                        Текущий план
                    </button>
                    <button
                        v-else-if="planItem.is_coming_soon"
                        type="button"
                        class="plan-cta plan-cta--soon"
                        disabled
                    >
                        Скоро
                    </button>
                    <button
                        v-else
                        type="button"
                        class="plan-cta"
                        :class="{ 'plan-cta--primary': planItem.code === accentCode }"
                        @click="$emit('select', planItem.code)"
                    >
                        {{ ctaLabel(planItem) }}
                    </button>
                </article>
            </div>

        <template #footer>
            <div class="modal-foot">
                <span>Безопасная оплата — Visa / MasterCard / МИР / СБП</span>
                <span>Чек и реквизиты — на email</span>
            </div>
        </template>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import AmModal from '@/components/ui/AmModal.vue'
import { getChatBillingPlans } from '@/utils/chatApi'

const props = defineProps({
    open: { type: Boolean, default: false },
    currentPlan: { type: String, default: 'free' },
})

defineEmits(['close', 'select'])

const plans = ref([])
const loading = ref(false)
const error = ref(false)
let loaded = false

async function loadPlans() {
    loading.value = true
    error.value = false
    try {
        plans.value = await getChatBillingPlans()
        loaded = true
    } catch (e) {
        error.value = true
        plans.value = []
    } finally {
        loading.value = false
    }
}

watch(
    () => props.open,
    (next) => {
        if (next && !loaded) {
            loadPlans()
        }
    },
    { immediate: true },
)

const visiblePlans = computed(() => plans.value)

// «Популярный» — определяем как «не текущий, не coming_soon, с минимальной положительной ценой».
// Так бейдж сам прицепится к Pro, без хардкода кода тарифа в шаблоне.
const accentCode = computed(() => {
    const candidates = visiblePlans.value.filter(
        (p) => !p.is_coming_soon && p.price_cents > 0 && p.code !== props.currentPlan,
    )
    if (candidates.length === 0) return null
    const cheapestPaid = candidates.reduce(
        (acc, p) => (acc === null || p.price_cents < acc.price_cents ? p : acc),
        null,
    )
    return cheapestPaid?.code || null
})

function formatPrice(plan) {
    if (plan.is_coming_soon) return 'Скоро'
    if (!plan.price_cents) return '0 ₽'
    const rub = Math.round(plan.price_cents / 100)
    return `${rub.toLocaleString('ru-RU')} ₽`
}

function formatPriceSuffix(plan) {
    if (plan.is_coming_soon) return ''
    if (!plan.period_days) return '/мес'
    if (plan.period_days === 30) return '/мес'
    if (plan.period_days === 365 || plan.period_days === 360) return '/год'
    return `/${plan.period_days} дн`
}

function formatPlanNote(plan) {
    if (plan.description) return plan.description
    if (!plan.price_cents) return 'Бесплатно'
    return ''
}

function ctaLabel(plan) {
    if (!plan.price_cents) return 'Перейти на ' + plan.name
    return 'Оформить ' + plan.name
}

function formatRequestsLimit(plan) {
    if (!plan.requests_per_day) return 'Без лимита запросов в день'
    return `${plan.requests_per_day.toLocaleString('ru-RU')} запросов / день`
}

function formatTokensLimit(plan) {
    if (!plan.tokens_per_month) return null
    const tokens = plan.tokens_per_month
    let pretty
    if (tokens >= 1_000_000) {
        pretty = `${(tokens / 1_000_000).toLocaleString('ru-RU', { maximumFractionDigits: 1 })} млн`
    } else if (tokens >= 1_000) {
        pretty = `${(tokens / 1_000).toLocaleString('ru-RU', { maximumFractionDigits: 0 })} тыс.`
    } else {
        pretty = `${tokens}`
    }
    return `${pretty} токенов / мес`
}

function formatHistory(plan) {
    if (!plan.max_thread_age_days) return null
    const days = plan.max_thread_age_days
    if (days >= 365) return `История ${Math.round(days / 365)} год${days >= 365 * 2 ? 'а' : ''}`
    if (days >= 30) return `История ${Math.round(days / 30)} мес`
    return `История ${days} дн`
}

function formatSources(plan) {
    const sources = plan.sources || []
    if (sources.length === 0) return null
    const names = sources.map((s) => s.name).filter(Boolean)
    if (names.length === 0) return null
    return `Источники: ${names.join(', ')}`
}

function buildFeatures(plan) {
    const features = []
    features.push({ text: formatRequestsLimit(plan) })
    const tokens = formatTokensLimit(plan)
    if (tokens) features.push({ text: tokens })
    const history = formatHistory(plan)
    if (history) features.push({ text: history })
    const sources = formatSources(plan)
    if (sources) features.push({ text: sources })
    features.push({
        text: 'Экспорт PDF / Excel / Share-link',
        off: !plan.allow_share,
    })
    features.push({
        text: 'Сохранённые отчёты «Мои отчёты»',
        off: !plan.allow_my_reports,
    })
    return features
}
</script>

<style scoped>
.state-block {
    padding: 36px 20px;
    text-align: center;
    color: #5F5E5A;
    font-size: 13px;
    background: #FAFAF7;
    border-radius: 10px;
}

.state-block--error {
    color: #C0050F;
    background: #FFEAE7;
}

.plans {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
}

.plan-card {
    position: relative;
    border: 1px solid #E8E6E0;
    border-radius: 12px;
    padding: 18px 18px 16px;
    background: #FAFAF7;
    display: flex;
    flex-direction: column;
}

.plan-card--accent {
    border-color: #23282C;
    background: white;
    box-shadow: 0 6px 18px rgba(21, 26, 34, 0.08);
}

.plan-card--current {
    border-color: #198FA0;
    background: rgba(25, 143, 160, 0.05);
}

.plan-card--coming-soon {
    background: repeating-linear-gradient(
        135deg,
        #FAFAF7,
        #FAFAF7 12px,
        #F1EFE8 12px,
        #F1EFE8 24px
    );
    border-color: #E8E6E0;
}

.plan-tag {
    position: absolute;
    top: -10px;
    left: 18px;
    background: #23282C;
    color: white;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    padding: 4px 8px;
    border-radius: 4px;
}

.plan-tag--soon {
    background: #B07E18;
}

.plan-name {
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #23282C;
    margin-bottom: 6px;
}

.plan-price {
    display: flex;
    align-items: baseline;
    gap: 4px;
    margin-bottom: 4px;
}

.price-value {
    font-size: 26px;
    font-weight: 700;
    color: #23282C;
    letter-spacing: -0.3px;
}

.price-suffix {
    font-size: 13px;
    color: #5F5E5A;
}

.plan-note {
    font-size: 11.5px;
    color: #888780;
    margin-bottom: 14px;
    min-height: 16px;
    line-height: 1.4;
}

.plan-features {
    list-style: none;
    margin: 0 0 16px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
    flex: 1;
}

.plan-feature {
    display: flex;
    gap: 8px;
    font-size: 12.5px;
    color: #23282C;
    line-height: 1.45;
}

.plan-feature--off {
    color: #B4B2A9;
    text-decoration: line-through;
}

.feature-mark {
    flex-shrink: 0;
    width: 16px;
    text-align: center;
    color: #198FA0;
    font-weight: 600;
}

.plan-feature--off .feature-mark {
    color: #C4C2BC;
}

.plan-cta {
    width: 100%;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    padding: 10px 14px;
    border-radius: 8px;
    border: 1px solid #23282C;
    background: white;
    color: #23282C;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;
}

.plan-cta:hover {
    background: #23282C;
    color: white;
}

.plan-cta--primary {
    background: #E30613;
    border-color: #E30613;
    color: white;
}

.plan-cta--primary:hover {
    background: #c30410;
    border-color: #c30410;
    color: white;
}

.plan-cta--current {
    background: rgba(25, 143, 160, 0.12);
    border-color: rgba(25, 143, 160, 0.4);
    color: #198FA0;
    cursor: default;
}

.plan-cta--current:hover {
    background: rgba(25, 143, 160, 0.12);
    color: #198FA0;
}

.plan-cta--soon {
    background: rgba(176, 126, 24, 0.12);
    border-color: rgba(176, 126, 24, 0.4);
    color: #8A6810;
    cursor: not-allowed;
}

.plan-cta--soon:hover {
    background: rgba(176, 126, 24, 0.12);
    color: #8A6810;
}

.modal-foot {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 8px;
    margin-top: 18px;
    padding-top: 14px;
    border-top: 1px solid #F1EFE8;
    font-size: 11.5px;
    color: #888780;
    letter-spacing: 0.4px;
}

@media (max-width: 820px) {
    .plans {
        grid-template-columns: 1fr;
    }

    .modal {
        padding: 22px;
    }

    .modal-title {
        font-size: 19px;
    }
}
</style>
